import { DEFAULT_INITIAL_PAGINATION_DATA } from "@saleor/config";
import { useDispatchListQuery } from "@saleor/dispatch/queries";
import { DispatchListUrlQueryParams } from "@saleor/dispatch/url";
import useListSettings from "@saleor/hooks/useListSettings";
import useNavigator from "@saleor/hooks/useNavigator";
import usePaginator, {
  createPaginationState
} from "@saleor/hooks/usePaginator";
import { maybe } from "@saleor/misc";
import { ListViews } from "@saleor/types";
import {
  OrderDirection,
  RiderDispatchBatchSortField
} from "@saleor/types/globalTypes";
import * as React from "react";
import { useEffect } from "react";

import { getFilterVariables } from "../../components/DispatchHistoryListPage/filters";
import DispatchHistoryListPage from "./../../components/DispatchHistoryListPage";
import {
  batchAssignmentListPath,
  dispatchHistoryListUrl,
  tripInformationUrl
} from "./../../url";

interface DispatchHistoryProps {
  params: DispatchListUrlQueryParams;
}

const DispatchHistory: React.FC<DispatchHistoryProps> = ({ params }) => {
  const navigate = useNavigator();
  const paginate = usePaginator();

  const { updateListSettings, settings } = useListSettings(
    ListViews.DISPATCH_LIST
  );

  const paginationState = createPaginationState(settings.rowNumber, params);

  const queryVariables = React.useMemo(
    () => ({
      ...paginationState,
      filter: getFilterVariables(params)
    }),
    [params, settings.rowNumber]
  );

  useEffect(
    () =>
      navigate(
        dispatchHistoryListUrl({
          ...params,
          ...DEFAULT_INITIAL_PAGINATION_DATA
        }),
        true
      ),
    [settings.rowNumber]
  );

  const { data, loading } = useDispatchListQuery({
    displayLoader: true,
    variables: {
      ...queryVariables,
      sortBy: {
        direction: OrderDirection.DESC,
        field: RiderDispatchBatchSortField.CREATION_DATE
      }
    }
  });

  const { loadNextPage, loadPreviousPage, pageInfo } = paginate(
    maybe(() => data.riderDispatchBatches.pageInfo),
    paginationState,
    params
  );

  return (
    <DispatchHistoryListPage
      params={params}
      onRowClick={id => () => navigate(tripInformationUrl(id))}
      navToAssignRider={() => navigate(batchAssignmentListPath)}
      batches={maybe(() =>
        data?.riderDispatchBatches?.edges
          ? data?.riderDispatchBatches?.edges.map(edge => edge.node)
          : []
      )}
      settings={settings}
      disabled={loading}
      pageInfo={pageInfo}
      onNextPage={loadNextPage}
      onPreviousPage={loadPreviousPage}
      onUpdateListSettings={updateListSettings}
    />
  );
};

export default DispatchHistory;
